import { notify } from '@servepod/ui';

/**
 * Admin API client.
 *
 * Separate from the console's client rather than shared, because the two have
 * genuinely different failure semantics: a 403 in the console means "ask your admin",
 * while a 403 here means "you are not staff" and must send the user away rather than
 * showing them an empty dashboard.
 */

/**
 * Failures of an MFA ATTEMPT, as opposed to failures of the session.
 *
 * Each is a reason to keep the user where they are and tell them what happened:
 * the code was wrong, they are briefly locked out, or the server cannot decrypt
 * its TOTP secrets and they should reach for a recovery code.
 */
const MFA_ATTEMPT_CODES = new Set([
  'INVALID_CREDENTIALS',
  'RATE_LIMITED',
  'DEPENDENCY_UNAVAILABLE',
]);

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly requestId: string;
  readonly details: Record<string, unknown>;

  constructor(
    status: number,
    body: {
      error: {
        code: string;
        message: string;
        request_id: string;
        details: Record<string, unknown>;
      };
    },
  ) {
    super(body.error.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.error.code;
    this.requestId = body.error.request_id;
    this.details = body.error.details;
  }
}

let onDenied: (() => void) | undefined;

export function setDeniedHandler(handler: () => void): void {
  onDenied = handler;
}

let onMfaRequired: (() => void) | undefined;

/** Opens the step-up dialog; MFA_REQUIRED must never be treated as sign-out. */
export function setMfaRequiredHandler(handler: () => void): void {
  onMfaRequired = handler;
}

export async function api<T>(
  path: string,
  options: { method?: 'GET' | 'POST' | 'PUT' | 'DELETE'; body?: unknown; quiet?: boolean } = {},
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: options.method ?? 'GET',
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        ...(options.body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    });
  } catch {
    const offline = new ApiError(0, {
      error: {
        code: 'NETWORK_UNAVAILABLE',
        message: 'Could not reach the API.',
        request_id: '',
        details: {},
      },
    });
    if (options.quiet !== true) notify.error('Connection lost', offline.message);
    throw offline;
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text.length > 0 ? JSON.parse(text) : undefined;

  if (!response.ok) {
    const error = new ApiError(response.status, payload);
    // MFA_REQUIRED is neither of the cases below: the session is real and staff,
    // it just needs a fresh code — summon the challenge dialog instead of ending
    // the session.
    if (error.code === 'MFA_REQUIRED') {
      onMfaRequired?.();
    } else if (MFA_ATTEMPT_CODES.has(error.code)) {
      // A wrong code, a lockout, or a broken server-side key. All arrive as 401
      // or 4xx and NONE of them mean the session is gone — the cookie is still
      // perfectly valid. Falling through to onDenied signed the operator out of
      // the console over a single typo in the step-up dialog, recoverable only
      // by a full page reload. The caller shows the reason instead.
    } else if (response.status === 401 || response.status === 403) {
      // 401 means no session; 403 means a session without staff authority. Both end
      // the admin experience, but only the second is worth explaining.
      onDenied?.();
    } else if (options.quiet !== true) {
      notify.error('Request failed', error.message);
    }
    throw error;
  }

  return payload as T;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FleetOverview {
  accelerators: {
    total: number;
    available: number;
    reserved: number;
    allocated: number;
    cleaning: number;
    quarantined: number;
    unhealthy: number;
    offline: number;
    maintenance: number;
    draining: number;
  };
  byVendor: { vendor: string; model: string; total: number; available: number }[];
  nodes: {
    total: number;
    ready: number;
    suspect: number;
    offline: number;
    draining: number;
    maintenance: number;
  };
  pods: { running: number; starting: number; stopped: number; failed: number; total: number };
  regions: { region: string; nodes: number; accelerators: number; available: number }[];
  accounts: { total: number; active: number; suspended: number };
  utilisationPercent: number;
}

export interface Revenue {
  monthToDateRevenue: string;
  activeAccounts: number;
  billableAccelerators: number;
  topAccounts: { name: string; spend: string }[];
  dailyRevenue: { day: string; amount: string }[];
}

export interface NodeRow {
  id: string;
  region: string;
  hostname: string;
  state: string;
  providerClass: string;
  availabilityZone: string;
  cpuCores: number;
  memoryGb: number;
  diskGb: number;
  acceleratorTotal: number;
  acceleratorAvailable: number;
  acceleratorAllocated: number;
  reliabilityScore: number;
  networkScore: number;
  lastHeartbeatAt: string | null;
  heartbeatAgeSeconds: number | null;
  generation: number;
}

export interface AcceleratorRow {
  id: string;
  region: string;
  nodeHostname: string;
  vendor: string;
  model: string;
  architecture: string;
  memoryGb: number;
  state: string;
  health: string;
  computeStack: string;
  driverVersion: string;
  pciAddress: string;
  vendorDeviceId: string;
  cleanupVerifiedAt: string | null;
  quarantineReason: string | null;
  podName: string | null;
}

export interface AdminPodRow {
  id: string;
  name: string;
  state: string;
  region: string;
  accountName: string;
  organizationName: string;
  offeringSlug: string;
  acceleratorCount: number;
  nodeHostname: string | null;
  createdAt: string;
  failureCode: string | null;
}

export interface AccountRow {
  id: string;
  name: string;
  status: string;
  createdAt: string;
  users: number;
  organizations: number;
  activePods: number;
  /** Live prepaid balance — negative when usage has outrun the ledger. */
  balance: string;
  creditAvailable: string;
  spendThisMonth: string;
  suspensionReason: string | null;
}

export interface AuditRow {
  id: string;
  action: string;
  outcome: string;
  actorEmail: string | null;
  actorMethod: string;
  resourceType: string | null;
  resourceId: string | null;
  reason: string | null;
  occurredAt: string;
}

export interface AdminMe {
  user: { id: string | null; email: string | null; display_name: string | null };
  account: { id: string; name: string | null };
  platform_roles: string[];
  is_staff: boolean;
}

export const admin = {
  me: () => api<AdminMe>('/v1/me'),
  login: (email: string, password: string) =>
    api<{ user_id: string }>('/v1/auth/login', {
      method: 'POST',
      body: { email, password },
      quiet: true,
    }),
  logout: () => api<void>('/v1/auth/logout', { method: 'POST' }),

  overview: () => api<{ fleet: FleetOverview; revenue: Revenue }>('/v1/admin/overview'),
  nodes: () => api<{ nodes: NodeRow[] }>('/v1/admin/nodes').then((r) => r.nodes),
  accelerators: (filters: { state?: string; vendor?: string } = {}) => {
    const query = new URLSearchParams();
    if (filters.state) query.set('state', filters.state);
    if (filters.vendor) query.set('vendor', filters.vendor);
    const suffix = query.toString();
    return api<{ accelerators: AcceleratorRow[] }>(
      `/v1/admin/accelerators${suffix ? `?${suffix}` : ''}`,
    ).then((r) => r.accelerators);
  },
  pods: () => api<{ pods: AdminPodRow[] }>('/v1/admin/pods').then((r) => r.pods),
  accounts: () => api<{ accounts: AccountRow[] }>('/v1/admin/accounts').then((r) => r.accounts),
  audit: (limit = 150) =>
    api<{ events: AuditRow[] }>(`/v1/admin/audit?limit=${limit}`).then((r) => r.events),
  revenue: () => api<{ revenue: Revenue }>('/v1/admin/revenue').then((r) => r.revenue),

  drainNode: (id: string, region: string, reason: string) =>
    api<void>(`/v1/admin/nodes/${id}/drain?region=${encodeURIComponent(region)}`, {
      method: 'POST',
      body: { reason },
      quiet: true,
    }),
  undrainNode: (id: string, region: string, reason: string) =>
    api<void>(`/v1/admin/nodes/${id}/undrain?region=${encodeURIComponent(region)}`, {
      method: 'POST',
      body: { reason },
      quiet: true,
    }),
  quarantineAccelerator: (id: string, region: string, reason: string) =>
    api<void>(`/v1/admin/accelerators/${id}/quarantine?region=${encodeURIComponent(region)}`, {
      method: 'POST',
      body: { reason },
      quiet: true,
    }),
  validateAccelerator: (id: string, region: string, reason: string) =>
    api<void>(`/v1/admin/accelerators/${id}/validate?region=${encodeURIComponent(region)}`, {
      method: 'POST',
      body: { reason },
      quiet: true,
    }),
  suspendAccount: (id: string, reason: string) =>
    api<void>(`/v1/admin/accounts/${id}/suspend`, {
      method: 'POST',
      body: { reason },
      quiet: true,
    }),
  reinstateAccount: (id: string, reason: string) =>
    api<void>(`/v1/admin/accounts/${id}/reinstate`, {
      method: 'POST',
      body: { reason },
      quiet: true,
    }),
  adjustAccount: (id: string, amountUsd: number, reason: string) =>
    api<{ amount: string; balance: string }>(`/v1/admin/accounts/${id}/adjustments`, {
      method: 'POST',
      body: { amount_usd: amountUsd, reason },
      quiet: true,
    }),

  billingRates: () => api<{ base: string; rates: CurrencyRateRow[] }>('/v1/admin/billing/rates'),
  updateBillingRate: (input: { currency: string; rate: number; reason: string }) =>
    api<CurrencyRateRow>('/v1/admin/billing/rates', { method: 'PUT', body: input, quiet: true }),

  billingSettings: () => api<BillingSettingsRow>('/v1/admin/billing/settings'),
  updateBillingSettings: (input: {
    floor_usd: number;
    grace_hours: number;
    warn_usd: number;
    reason: string;
  }) =>
    api<BillingSettingsRow>('/v1/admin/billing/settings', {
      method: 'PUT',
      body: input,
      quiet: true,
    }),

  mfaStatus: () => api<MfaStatus>('/v1/auth/mfa'),
  mfaBegin: () =>
    api<{ secret: string; otpauth_uri: string }>('/v1/auth/mfa/totp/begin', {
      method: 'POST',
      quiet: true,
    }),
  mfaActivate: (code: string) =>
    api<{ recovery_codes: string[] }>('/v1/auth/mfa/totp/activate', {
      method: 'POST',
      body: { code },
      quiet: true,
    }),
  mfaChallenge: (code: string) =>
    api<{ satisfied_until: string }>('/v1/auth/mfa/challenge', {
      method: 'POST',
      body: { code },
      quiet: true,
    }),
  mfaRemove: (code: string) =>
    api<{ enrolled: boolean }>('/v1/auth/mfa/totp', {
      method: 'DELETE',
      body: { code },
      quiet: true,
    }),
};

export interface CurrencyRateRow {
  currency: string;
  /** Foreign units per 1 USD, decimal string. */
  rate: string;
  source: 'CONFIGURED' | 'DEFAULT';
  updated_at?: string | null;
}

export interface BillingSettingsRow {
  /** Decimal dollars, ≤ 0 — the balance at which pods are stopped. */
  floor: string;
  grace_hours: number;
  warn_below: string;
  updated_by: string | null;
  updated_at: string | null;
}

export interface MfaStatus {
  enrolled: boolean;
  pending: boolean;
  recovery_codes_remaining: number;
  elevation_ttl_seconds: number;
}
